import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDropDown, ArrowDropUp, Error } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField, Typography, } from "@mui/material";
import { AsYouType, getCountries, getCountryCallingCode, isValidPhoneNumber, ParseError, parsePhoneNumber, parsePhoneNumberCharacter, parsePhoneNumberWithError, validatePhoneNumberLength, } from "libphonenumber-js";
import { useMemo, useState, useRef, useCallback, useEffect, } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import StyledAutocomplete from "./styledAutocomplete";
import { onKeyDown, onChange } from "input-format";
/**
 *
 * @param {string} countryCode Flag's corresponding country
 * @returns {string} Flag
 */
function getFlagEmoji(countryCode) {
    const codePoints = countryCode
        .toUpperCase()
        .split("")
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}
/**
 *
 * @param {NerdPhoneFieldProps} props Component props
 * @returns {ReactElement} NerdPhoneField
 */
export default function NerdPhoneField(props) {
    const { textFieldProps, onAccept, defaultCountry = "US", value, format = "international", onError, } = props;
    const { t } = useTranslation("components", { i18n });
    const regionNames = useMemo(() => new Intl.DisplayNames([i18n.language], {
        type: "region",
    }), [i18n.language]);
    const countriesWithCallingCode = useMemo(() => {
        const data = {};
        const countries = getCountries();
        countries.forEach((country) => {
            data[country] = {
                countryCode: country,
                callingCode: getCountryCallingCode(country),
                fullName: regionNames.of(country) || "",
                flag: getFlagEmoji(country),
            };
        });
        return data;
    }, []);
    const [countryValue, setCountryValue] = useState(countriesWithCallingCode[value != undefined && isValidPhoneNumber(value)
        ? parsePhoneNumber(value).country || defaultCountry
        : defaultCountry]);
    const [textFieldValue, setTextFieldValue] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [error, setError] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    /**
     * Close contries list
     */
    function handleClose() {
        setAnchorEl(null);
    }
    /**
     * Validate Phone Number
     *
     * @param {string} value Phone Number
     * @param {CountryWithCallingCode | undefined} countryValue Phone Number Country
     */
    function validatePhoneNumber(value, countryValue) {
        if (value != "") {
            const result = validatePhoneNumberLength(value, countryValue?.countryCode);
            let errorMsg = "";
            switch (result) {
                case "INVALID_COUNTRY":
                    errorMsg = t("nerdPhoneField.invalid_country");
                    break;
                case "INVALID_LENGTH":
                    errorMsg = t("nerdPhoneField.invalid_length");
                    break;
                case "NOT_A_NUMBER":
                    errorMsg = t("nerdPhoneField.invalid_phone_number");
                    break;
                case "TOO_LONG":
                    errorMsg = t("nerdPhoneField.too_long_phone_number");
                    break;
                case "TOO_SHORT":
                    errorMsg = t("nerdPhoneField.too_short_phone_number");
                    break;
                case undefined: {
                    const phoneNumber = parsePhoneNumber(value, countryValue?.countryCode);
                    onAccept?.(format == "international"
                        ? phoneNumber.formatInternational()
                        : phoneNumber.formatNational());
                    break;
                }
                default:
                    break;
            }
            setErrorMsg(errorMsg);
            if (!result && countryValue) {
                setError(false);
            }
            else {
                setError(true);
                onError?.(errorMsg);
            }
        }
        else {
            setCountryValue(countriesWithCallingCode[defaultCountry]);
            onAccept?.("");
            setErrorMsg("");
            setError(false);
        }
    }
    /**
     *
     * @param {string} value value
     */
    function onTextFieldChange(value) {
        const formatter = new AsYouType(countryValue?.countryCode || defaultCountry);
        // Format the number.
        const text = formatter.input("" + value);
        let country = countryValue;
        try {
            const phoneNumber = parsePhoneNumberWithError(text, countryValue?.countryCode);
            //Update countryValue if inputted calling code is different
            if (phoneNumber.country) {
                if (phoneNumber.country != countryValue?.countryCode) {
                    country = countriesWithCallingCode[phoneNumber.country];
                    setCountryValue(country);
                }
            }
            else {
                country = undefined;
                setCountryValue(undefined);
            }
            // //Avoid formatting phone number when deleting parenthesis
            // if (!(newVal.indexOf("(") != -1 && newVal.indexOf(")") == -1))
            //   newVal = formatIncompletePhoneNumber(newVal, phoneNumber.country);
        }
        catch (error) {
            if (!(error instanceof ParseError)) {
                throw error;
            }
        }
        setTextFieldValue(text);
        validatePhoneNumber(text, country);
    }
    /**
     *
     * @param {CountryWithCallingCode} newValue New Value
     */
    function onAutocompleteChange(newValue) {
        let newPhoneNumber = "" + internalRef.current?.value;
        if (countryValue && newValue.callingCode != countryValue.callingCode) {
            newPhoneNumber =
                "+" +
                    newValue.callingCode +
                    internalRef.current?.value.substring(countryValue.callingCode.length + 1, internalRef.current?.value.length);
        }
        setCountryValue(newValue);
        validatePhoneNumber(newPhoneNumber, newValue);
    }
    useEffect(() => {
        if (value != undefined && value != internalRef.current?.value) {
            const val = new AsYouType(defaultCountry).input(value || "");
            if (isValidPhoneNumber(value)) {
                const _country = parsePhoneNumber(value).country;
                const country = _country
                    ? countriesWithCallingCode[_country]
                    : undefined;
                if (country) {
                    setCountryValue(country);
                }
            }
            setTextFieldValue(val);
        }
    }, [value]);
    const inputFormat = useCallback((value) => {
        // "As you type" formatter.
        const formatter = new AsYouType(countryValue?.countryCode || defaultCountry);
        // Format the number.
        const text = formatter.input("" + value);
        const template = formatter.getTemplate();
        return {
            text,
            template,
        };
    }, [countryValue, defaultCountry]);
    const internalRef = useRef(null);
    const _onChange = useCallback((event) => {
        if (internalRef.current)
            return onChange(event, internalRef.current, parsePhoneNumberCharacter, inputFormat, onTextFieldChange);
    }, [internalRef, inputFormat, onTextFieldChange]);
    const _onKeyDown = useCallback((event) => {
        if (internalRef.current)
            return onKeyDown(event, internalRef.current, parsePhoneNumberCharacter, inputFormat, onTextFieldChange);
    }, [internalRef, inputFormat, onTextFieldChange]);
    return (_jsx(TextField, { inputRef: internalRef, error: error, helperText: error ? errorMsg : "", ...textFieldProps, value: textFieldValue, inputProps: {
            onChange: _onChange,
            onKeyDown: _onKeyDown,
        }, InputProps: {
            startAdornment: (_jsxs(InputAdornment, { position: "start", children: [_jsxs("div", { style: {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }, children: [countryValue ? (_jsx(Typography, { color: "initial", children: countryValue.flag })) : (_jsx(Error, { color: "error", fontSize: "small" })), _jsx(IconButton, { onClick: (event) => setAnchorEl(event.currentTarget.parentElement), sx: { ml: 1 }, "aria-label": t("nerdPhoneField.select_country"), size: "small", children: open ? (_jsx(ArrowDropUp, { fontSize: "small" })) : (_jsx(ArrowDropDown, { fontSize: "small" })) })] }), _jsx(StyledAutocomplete, { anchorEl: anchorEl, handleClose: handleClose, onChange: (_e, newValue) => {
                            onAutocompleteChange(newValue);
                        }, open: open, options: Object.values(countriesWithCallingCode), value: countryValue })] })),
        } }));
}
